import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../../helper/ApiEndpoints";
import ApiRequest from "../../../helper/ApiRequest";
import CustomSelect from "../../../component/CustomSelect";
import CustomImageUpload from "../../../component/CustomImageUpload";
import DynamicCKEditor from "../../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../../component/AdminHeader";

const addEndpoint = ApiEndpoints.project.create;
const categoryEndpoint = ApiEndpoints.projectType.list;

export default function AddProject() {
  const navigate = useNavigate();

  const [projectTypes, setProjectTypes] = useState([]);

  const [previews, setPreviews] = useState({ thumbnail: null, background_image:null, images: [null] });
  const [postData, setPostData] = useState({title: "", slug: "", continent: "", country: "", projecttype_id: "", description: "", thumbnail: "", alt_thumbnail: "", background_image: "", status: "0", images: [{ image: null, alt_image: "" }, { image: null, alt_image: "" }] });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await ApiRequest("GET", categoryEndpoint);
        setProjectTypes(response?.project_types || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setProjectTypes([]);
      }
    };
    fetchCategories();
  }, []);

  const handleImageChange = (index, file) => {
    const updatedImages = [...postData.images];
    updatedImages[index].image = file;

    setPostData(prev => ({
      ...prev,
      images: updatedImages
    }));

    const updatedPreviews = [...previews.images];
    updatedPreviews[index] = URL.createObjectURL(file);

    setPreviews(prev => ({
      ...prev,
      images: updatedPreviews
    }));
  };

  const handleAltChange = (index, value) => {
    const updatedImages = [...postData.images];
    updatedImages[index].alt_image = value;

    setPostData(prev => ({
      ...prev,
      images: updatedImages
    }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      // Update the previews state
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      // Update the postData state
      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      // For non-file inputs, update the postData state directly
      setPostData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };

  const isEmptyValue = (value) => {
    if (value === null || value === "") return true;

    // allow File objects
    if (value instanceof File) return false;

    // arrays
    if (Array.isArray(value)) {
      return value.some(item => isEmptyValue(item));
    }

    // objects
    if (typeof value === "object") {
      return Object.values(value).some(v => isEmptyValue(v));
    }

    return false;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(postData)) {
      if (key === "location") continue;

      if (isEmptyValue(value)) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    const imagesPayload = postData.images.map((item, index) => ({
      image: item.image ? item.image.name : "",
      alt_image: item.alt_image
    }));

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (key !== "images") {
        formData.append(key, value);
      }
      if (key === "images") {
        formData.append(key, JSON.stringify(imagesPayload))
      }
    });

    postData.images.forEach((item, index) => {
      if (item.image) {
        formData.append(`images[${index}][image]`, item.image);
      }
    });

    try {
      const response = await ApiRequest("POST", addEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.error === false) {
        toast.success(response.message);
        navigate("/project");
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // =================Add More Functionality Start=====================

  const addImageBlock = () => {
    setPostData(prev => ({
      ...prev,
      images: [...prev.images, { image: null, alt_image: "" }]
    }));

    setPreviews(prev => ({
      ...prev,
      images: [...prev.images, null]
    }));
  };

  const removeImageBlock = (index) => {
    if (index === 0) return; // 🚫 first one protected

    setPostData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));

    setPreviews(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };


  const pagename = {
    name: 'Project Management',
    user: 'Admin',
    innerpage: 'Project Management / Project / Add Project'
  }

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="projectAdd" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>title<small>*</small></label>
                  </div>
                  <input value={postData.title} onChange={handleChange} name="title" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input value={postData?.slug} onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-4">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Continent<small>*</small></label>
                  </div>
                  <input value={postData?.continent} onChange={handleChange} name="continent" type="text" className="form-control" placeholder="Enter continent" />
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Country<small>*</small></label>
                  </div>
                  <input value={postData?.country} onChange={handleChange} name="country" type="text" className="form-control" placeholder="Enter country" />
                </div>
              </div>

              <div className="col-lg-4">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Project Type<small>*</small></label>
                  </div>
                  <CustomSelect defaultValue={postData?.projecttype_id} onChange={handleChange} name="projecttype_id" options={projectTypes} placeholder="Select Project Type" />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description} onChange={(event, editor) => handleEditorChange("description", editor?.getData())} />
                </div>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image (712 × 800)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={712}
                    requiredHeight={800}
                    imagePreview={previews.thumbnail} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input value={postData.alt_thumbnail} onChange={handleChange} name="alt_thumbnail" type="text" className="form-control" placeholder="alt thumbnail" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Background Image (1920 × 997)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="background_image"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    requiredWidth={1920}
                    requiredHeight={997}
                    imagePreview={previews.background_image} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <h5>Gallery Image</h5>

              {postData.images.map((item, index) => (
                <div className="col-lg-6" key={index}>
                  <div className="add-page-inputs">

                    <div className="label-wrap-add">
                      <label>Gallery Image {index + 1} (736 × 650)<small>*</small></label>
                    </div>

                    <CustomImageUpload name={`image_${index}`} acceptedFormats={[".webp"]} imagePreview={previews.images[index]} onImageChange={(e) => handleImageChange(index, e.target.files[0])} requiredWidth={736} requiredHeight={650}/>

                    <div className="label-wrap-add">
                      <label>Alt Tag Image {index + 1}<small>*</small></label>
                    </div>

                    <input type="text" className="form-control mt-2" placeholder="Alt Tag" value={item.alt_image} onChange={(e) => handleAltChange(index, e.target.value)} />

                    {index > 1 && (
                      <button type="button" className="btn btn-sm btn-danger mt-2" onClick={() => removeImageBlock(index)}>
                        Remove
                      </button>
                    )}

                  </div>
                </div>
              ))}

              <div className="col-lg-12 add-page-inputs">
                <button type="button" className="addmore-images w-100" onClick={addImageBlock}>
                  Add Image +
                </button>
              </div>
            </div>
          </div>

        </form>
        <button type="submit" form="projectAdd" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
